import { Suspense, lazy, Fragment } from "react";
import { Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import "react-toastify/dist/ReactToastify.css";
import { ICustomerConfiguration } from "@/interfaces/ICustomerConfiguration";
import useStore from "@store";
import DebugLinks from "./components/Debug/DebugLinks";
import { Box } from "@mui/material";
import { ensureLeadingSlash } from "./tools/tools";
import MetaTags from "./components/Seo/MetaTags";

//views
const QuickSearch = lazy(() => import("@/views/QuickSearch/PageQuickSearch"));
const QuickSearchUsed = lazy(() => import("@/views/QuickSearch/components/QuickSearchUsed"));
const QuickSearchNew = lazy(() => import("@/views/QuickSearch/components/QuickSearchNew"));
const VehicleList = lazy(() => import("@/views/VehiclesList/PageVehicleList"));
const VehicleDetail = lazy(() => import("@/views/VehicleDetail/PageVehicleDetail"));
const DetailSearch = lazy(() => import("@/views/DetailSearch/PageDetailSearch"));
const Configurator = lazy(() => import("@/views/Admin/Configurator/PageConfigurator"));
const Login = lazy(() => import("@/views/Admin/Login/PageLogin"));
const Setup = lazy(() => import("@/views/Admin/Setup/PageSetup"));
const Faq = lazy(() => import("@/views/Admin/Setup/PageFaq"));
const Testo = lazy(() => import("@/views/Testo/PageTesto"));
const NotFoundPage = lazy(() => import("@/views/NotFound404/PageNotFound"));
const VehicleSlider = lazy(() => import("@/views/VehicleSlider/PageVehicleSlider"));

interface MainContainerProps {
  customerConfig: ICustomerConfiguration;
}

type ComponentType = React.LazyExoticComponent<React.FC<any>>;

const MainContainer: React.FC<MainContainerProps> = ({ customerConfig }) => {
  const { bearerToken } = useStore();
  const hasTestoQuery = new URLSearchParams(window.location.hash.split("?")[1] || window.location.search).get("testo") === "testo";

  // Component Mapping for dynamic route generation
  const componentMap: Record<string, ComponentType> = {
    QuickSearch,
    VehicleList,
    VehicleDetail,
    DetailSearch,
    VehicleSlider,
    QuickSearchUsed,
    QuickSearchNew
  };

  // Generate Routes from pagesNew
  const generateRoutesFromPagesNew = () => {
    if (!customerConfig.pagesNew) return null;

    return Object.entries(customerConfig.pagesNew).map(([key, pageConfig]) => {
      const { path, components, title, metaDescription, h1, h2 } = pageConfig;

      const routeElements = components.map((component, index) => {
        const normalizedComponentName = component.name.toLowerCase();
        const Component = Object.entries(componentMap).find(([name]) => name.toLowerCase() === normalizedComponentName)?.[1];
        if (!Component) return null;

        const props: Record<string, any> = {};
        if (component.filter) {
          props.filter = component.filter;
        }
        if (component.name.toLowerCase() === "configurator") {
          props.customerConfig = customerConfig;
        }
        if (title) {
          props.title = title;
        }
        if (metaDescription) {
          props.metaDescription = metaDescription;
        }
        if (h1) {
          props.h1 = h1;
        }
        if (h2) {
          props.h2 = h2;
        }

        return (
          <Fragment key={`${key}-${index}`}>
            <Box pb={6}>
              <Component {...props} />
            </Box>
          </Fragment>
        );
      });

      // Special case for vehicleDetail. Add params to path
      const isVehicleDetail = components.some((comp) => comp.name.toLowerCase() === "vehicledetail");
      const routePath = isVehicleDetail ? `${path}/:vehicleId/*` : path;

      return (
        <Route
          key={key}
          path={routePath}
          element={
            <>
              <MetaTags canonicalUrl={path} metaTitle={title} metaDescription={metaDescription} h1={h1} h2={h2} key={path} />
              {routeElements}
            </>
          }
        />
      );
    });
  };

  return (
    <>
      <Suspense
        fallback={
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress aria-label="Lädt..." />
          </Box>
        }
      >
        {import.meta.env.VITE_APP_DEBUG === "true" && <DebugLinks customerConfig={customerConfig} />}
        <Routes>
          {/* Admin Routes */}
          <Route path="/mpi-admin/config" element={bearerToken ? <Configurator customerConfig={customerConfig} /> : <Login />} />
          <Route path="/mpi-admin/login" element={<Login />} />
          {hasTestoQuery && (
            <>
              <Route path={ensureLeadingSlash(customerConfig?.pagesNew?.find((page) => page.name.toLowerCase() === "detailSearch".toLowerCase())?.path)} element={bearerToken ? <Configurator customerConfig={customerConfig} /> : <Login />} />
              <Route path={ensureLeadingSlash(customerConfig?.pages?.["detailSearch"])} element={bearerToken ? <Configurator customerConfig={customerConfig} /> : <Login />} />
              <Route path="/mpi-admin/login" element={<Login />} />
            </>
          )}

          {/* Debug Routes */}
          {import.meta.env.VITE_APP_DEBUG === "true" && (
            <>
              <Route path="/mpi-admin/setup" element={<Setup />} />
              <Route path="/mpi-admin/faq" element={<Faq />} />
              <Route path="/testo" element={<Testo />} />
            </>
          )}

          {/* All Routes from User Config */}
          {generateRoutesFromPagesNew()}
          <Route path={ensureLeadingSlash(customerConfig?.pagesNew?.find((page) => page.name.toLowerCase() === "vehicleDetail".toLowerCase())?.path)} element={<VehicleDetail />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </>
  );
};

export default MainContainer;
